import React, { useEffect, useRef, useState } from 'react';
import { getT } from '../i18n';
import { buildAssessmentScores, EXERCISE_IDS } from '../utils/assessmentScoring';
import { getVoiceSampleCandidates } from '../config/assessmentVoiceSamples';
import { API_URL } from '../config/api';
import { isHandheldDevice } from '../utils/deviceDetect';
import './AssessmentPage.css';

const TYPING_DURATION_SEC = 60;
const TYPING_TICK_MS = 100;

const countCorrectChars = (input, target) => {
  const max = Math.min(input.length, target.length);
  let correct = 0;
  for (let i = 0; i < max; i += 1) {
    if (input[i] === target[i]) correct += 1;
  }
  return correct;
};

const calcWpm = (charCount, elapsedSec) => {
  if (charCount <= 0 || elapsedSec <= 0) return 0;
  return Math.max(0, Math.round(((charCount / 5) * 60) / elapsedSec));
};

const calcAccuracy = (correct, total) => {
  if (total <= 0) return 0;
  return Math.round((correct / total) * 100);
};

const calcRoundScore = (wpm, accuracy) => Math.round(wpm * (accuracy / 100));

const EMPTY_TYPING_SESSION = {
  totalScore: 0,
  sentencesCompleted: 0,
  totalCorrect: 0,
  totalTyped: 0,
  bestWpm: 0,
};

const getExercises = (t) => [
  {
    id: 'greeting',
    title: t.ex1Title,
    script: t.ex1Script,
    tip: t.ex1Tip
  },
  {
    id: 'empathy',
    title: t.ex2Title,
    script: t.ex2Script,
    tip: t.ex2Tip
  },
  {
    id: 'hold',
    title: t.ex3Title,
    script: t.ex3Script,
    tip: t.ex3Tip
  },
  {
    id: 'closing',
    title: t.ex4Title,
    script: t.ex4Script,
    tip: t.ex4Tip
  }
];

const AssessmentPage = ({ lang = 'zh', onApply, userEmail = '', userName = '' }) => {
  const t = getT(lang).assessmentPage;
  const exercises = getExercises(t);
  const [activeId, setActiveId] = useState(exercises[0]?.id || '');
  const [mainTab, setMainTab] = useState('voice'); // voice | typing | form
  const [recordings, setRecordings] = useState({});
  const [exerciseMeta, setExerciseMeta] = useState(
    () => EXERCISE_IDS.reduce((acc, id) => ({ ...acc, [id]: { recorded: false, durationSec: 0 } }), {})
  );
  const [recordingId, setRecordingId] = useState(null);
  const [micError, setMicError] = useState('');
  const [formData, setFormData] = useState({
    fullName: userName || '',
    email: userEmail || '',
    completedTraining: '',
    englishConfidence: '7',
    notes: ''
  });
  const [formNotice, setFormNotice] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [assessmentSaved, setAssessmentSaved] = useState(false);
  const [gateNotice, setGateNotice] = useState('');
  const [isHandheld, setIsHandheld] = useState(() => isHandheldDevice());
  const [sampleSrc, setSampleSrc] = useState('');
  const [sampleReady, setSampleReady] = useState(false);
  const [sampleError, setSampleError] = useState(false);
  const [isSamplePlaying, setIsSamplePlaying] = useState(false);
  const [typingInput, setTypingInput] = useState('');
  const [typingTimeLeft, setTypingTimeLeft] = useState(TYPING_DURATION_SEC);
  const [typingElapsedSec, setTypingElapsedSec] = useState(0);
  const [typingStarted, setTypingStarted] = useState(false);
  const [typingFinished, setTypingFinished] = useState(false);
  const [promptIndex, setPromptIndex] = useState(0);
  const [typingSession, setTypingSession] = useState(EMPTY_TYPING_SESSION);
  const [scoredPromptIndices, setScoredPromptIndices] = useState(() => new Set());
  const [sentenceNotice, setSentenceNotice] = useState('');

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingsRef = useRef({});
  const recordStartedAtRef = useRef(0);
  const sampleAudioRef = useRef(null);
  const sampleCandidatesRef = useRef([]);
  const candidateIndexRef = useRef(0);
  const typingStartMsRef = useRef(null);
  const sentenceStartMsRef = useRef(null);
  const sentenceNoticeTimeoutRef = useRef(null);
  const gateNoticeTimeoutRef = useRef(null);
  const typingTargetRef = useRef('');
  const completingSentenceRef = useRef(false);
  const scoredPromptIndicesRef = useRef(new Set());

  const activeExercise = exercises.find((item) => item.id === activeId) || exercises[0];
  const completedCount = EXERCISE_IDS.filter((id) => exerciseMeta[id]?.recorded).length;
  const isVoiceComplete = completedCount >= EXERCISE_IDS.length;
  const isTypingComplete = typingFinished || typingSession.sentencesCompleted > 0;
  const canOpenTyping = isVoiceComplete || assessmentSaved;
  const canOpenForm = (isVoiceComplete && isTypingComplete) || assessmentSaved;
  // Phones / tablets may skip Assessment and go straight to Job Application.
  const canApply = assessmentSaved || isHandheld;

  useEffect(() => {
    const syncHandheld = () => setIsHandheld(isHandheldDevice());
    syncHandheld();
    window.addEventListener('resize', syncHandheld);
    window.addEventListener('orientationchange', syncHandheld);
    return () => {
      window.removeEventListener('resize', syncHandheld);
      window.removeEventListener('orientationchange', syncHandheld);
    };
  }, []);
  const typingPrompts = Array.isArray(t.typingPrompts) && t.typingPrompts.length > 0
    ? t.typingPrompts
    : [t.typingPrompt || ''];
  const typingTarget = typingPrompts[promptIndex % typingPrompts.length] || '';
  typingTargetRef.current = typingTarget;

  const selectMainTab = (tab) => {
    if (tab === 'typing' && !canOpenTyping) {
      setGateNotice(t.completeVoiceFirst);
      return;
    }
    if (tab === 'form' && !canOpenForm) {
      setGateNotice(isVoiceComplete ? t.completeTypingFirst : t.completeVoiceFirst);
      return;
    }
    setGateNotice('');
    setMainTab(tab);
  };

  const handleApplyClick = () => {
    if (!canApply) {
      setGateNotice(t.completeAssessmentFirst);
      if (!isVoiceComplete) setMainTab('voice');
      else if (!isTypingComplete) setMainTab('typing');
      else setMainTab('form');
      return;
    }
    if (typeof onApply === 'function') onApply();
  };

  useEffect(() => {
    recordingsRef.current = recordings;
  }, [recordings]);

  useEffect(() => {
    if (userEmail) {
      setFormData((prev) => ({ ...prev, email: userEmail }));
    }
    if (userName) {
      setFormData((prev) => ({ ...prev, fullName: userName }));
    }
  }, [userEmail, userName]);

  useEffect(() => {
    const email = String(userEmail || '').trim().toLowerCase();
    if (!email) return undefined;

    let cancelled = false;
    const loadSavedAssessment = async () => {
      try {
        const response = await fetch(`${API_URL}?action=get_assessment&email=${encodeURIComponent(email)}`);
        const result = await response.json();
        if (cancelled || result?.status !== 'success' || !result?.data) return;

        const saved = result.data;
        const meta = saved.exercise_meta && typeof saved.exercise_meta === 'object'
          ? saved.exercise_meta
          : {};

        setAssessmentSaved(true);
        setFormData((prev) => ({
          ...prev,
          fullName: saved.full_name || prev.fullName,
          email: saved.email || prev.email,
          completedTraining: saved.completed_training || prev.completedTraining,
          englishConfidence: String(saved.english_confidence ?? prev.englishConfidence),
          notes: saved.notes || prev.notes
        }));
        setExerciseMeta((prev) => {
          const next = { ...prev };
          EXERCISE_IDS.forEach((id) => {
            if (meta[id]) {
              next[id] = {
                recorded: Boolean(meta[id].recorded),
                durationSec: Number(meta[id].durationSec) || 0
              };
            }
          });
          return next;
        });
      } catch {
        // Ignore — applicant can still complete assessment fresh.
      }
    };

    loadSavedAssessment();
    return () => { cancelled = true; };
  }, [userEmail]);

  useEffect(() => {
    if (isVoiceComplete && gateNotice === t.completeVoiceFirst) {
      setGateNotice('');
    }
    if (isTypingComplete && gateNotice === t.completeTypingFirst) {
      setGateNotice('');
    }
    if (canApply && gateNotice === t.completeAssessmentFirst) {
      setGateNotice('');
    }
  }, [isVoiceComplete, isTypingComplete, canApply, gateNotice, t.completeVoiceFirst, t.completeTypingFirst, t.completeAssessmentFirst]);

  useEffect(() => {
    if (!gateNotice) {
      if (gateNoticeTimeoutRef.current) {
        clearTimeout(gateNoticeTimeoutRef.current);
        gateNoticeTimeoutRef.current = null;
      }
      return undefined;
    }

    if (gateNoticeTimeoutRef.current) {
      clearTimeout(gateNoticeTimeoutRef.current);
    }
    gateNoticeTimeoutRef.current = setTimeout(() => {
      setGateNotice('');
      gateNoticeTimeoutRef.current = null;
    }, 4000);

    return () => {
      if (gateNoticeTimeoutRef.current) {
        clearTimeout(gateNoticeTimeoutRef.current);
        gateNoticeTimeoutRef.current = null;
      }
    };
  }, [gateNotice]);

  useEffect(() => () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (sampleAudioRef.current) {
      sampleAudioRef.current.pause();
    }
    Object.values(recordingsRef.current).forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
    if (sentenceNoticeTimeoutRef.current) {
      clearTimeout(sentenceNoticeTimeoutRef.current);
    }
    if (gateNoticeTimeoutRef.current) {
      clearTimeout(gateNoticeTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!typingStarted || typingFinished) return undefined;

    const tick = () => {
      const startMs = typingStartMsRef.current;
      if (!startMs) return;
      const elapsed = (Date.now() - startMs) / 1000;
      const left = Math.max(0, TYPING_DURATION_SEC - elapsed);
      setTypingElapsedSec(elapsed);
      setTypingTimeLeft(Math.ceil(left));
      if (left <= 0) {
        setTypingFinished(true);
      }
    };

    tick();
    const timer = setInterval(tick, TYPING_TICK_MS);
    return () => clearInterval(timer);
  }, [typingStarted, typingFinished]);

  const stopProfessionalSample = () => {
    const audio = sampleAudioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsSamplePlaying(false);
  };

  useEffect(() => {
    if (!activeExercise?.id) return undefined;

    stopProfessionalSample();
    setSampleReady(false);
    setSampleError(false);
    candidateIndexRef.current = 0;
    const candidates = getVoiceSampleCandidates(lang, activeExercise.id);
    sampleCandidatesRef.current = candidates;
    setSampleSrc(candidates[0] || '');

    return () => stopProfessionalSample();
  }, [activeExercise?.id, lang]);

  const tryNextSampleCandidate = () => {
    const nextIndex = candidateIndexRef.current + 1;
    const candidates = sampleCandidatesRef.current;

    if (nextIndex < candidates.length) {
      candidateIndexRef.current = nextIndex;
      setSampleSrc(candidates[nextIndex]);
      setSampleReady(false);
      setSampleError(false);
      return;
    }

    setSampleError(true);
    setSampleReady(false);
  };

  const handleSampleLoaded = () => {
    const audio = sampleAudioRef.current;
    if (audio && audio.duration > 0) {
      setSampleReady(true);
      setSampleError(false);
    }
  };

  const handleSampleError = () => {
    tryNextSampleCandidate();
  };

  const playProfessionalSample = async () => {
    const audio = sampleAudioRef.current;
    if (!audio || sampleError || !sampleReady) return;

    try {
      stopProfessionalSample();
      audio.currentTime = 0;
      await audio.play();
      setIsSamplePlaying(true);
    } catch {
      setSampleError(true);
      setIsSamplePlaying(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecordingId(null);
  };

  const startRecording = async (exerciseId) => {
    setMicError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError(t.micUnsupported);
      return;
    }

    try {
      if (recordingId) stopRecording();
      stopProfessionalSample();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      chunksRef.current = [];
      recordStartedAtRef.current = Date.now();

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const durationSec = Math.max(1, Math.round((Date.now() - recordStartedAtRef.current) / 1000));
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setRecordings((prev) => {
          if (prev[exerciseId]) URL.revokeObjectURL(prev[exerciseId]);
          return { ...prev, [exerciseId]: url };
        });
        setExerciseMeta((prev) => ({
          ...prev,
          [exerciseId]: { recorded: true, durationSec }
        }));
      };

      recorder.start();
      setRecordingId(exerciseId);
    } catch {
      setMicError(t.micDenied);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const completeCurrentSentence = (input, target, currentPromptIndex) => {
    const correct = countCorrectChars(input, target);
    const accuracy = calcAccuracy(correct, input.length);
    const sentenceElapsed = sentenceStartMsRef.current
      ? Math.max(0.1, (Date.now() - sentenceStartMsRef.current) / 1000)
      : Math.max(0.1, typingElapsedSec);
    const wpm = calcWpm(input.length, sentenceElapsed);
    const roundScore = calcRoundScore(wpm, accuracy);
    const isFirstTry = !scoredPromptIndicesRef.current.has(currentPromptIndex);

    if (isFirstTry) {
      scoredPromptIndicesRef.current.add(currentPromptIndex);
      setScoredPromptIndices(new Set(scoredPromptIndicesRef.current));
      setTypingSession((prev) => ({
        totalScore: prev.totalScore + roundScore,
        sentencesCompleted: prev.sentencesCompleted + 1,
        totalCorrect: prev.totalCorrect + correct,
        totalTyped: prev.totalTyped + input.length,
        bestWpm: Math.max(prev.bestWpm, wpm),
      }));
      setSentenceNotice(t.typingSentenceDone);
    } else {
      setSentenceNotice(t.typingPracticeRound);
    }

    setTypingInput('');
    setPromptIndex((prev) => (prev + 1) % typingPrompts.length);
    sentenceStartMsRef.current = Date.now();
    if (sentenceNoticeTimeoutRef.current) clearTimeout(sentenceNoticeTimeoutRef.current);
    sentenceNoticeTimeoutRef.current = setTimeout(() => setSentenceNotice(''), 1800);
  };

  const handleTypingChange = (value) => {
    if (typingFinished || completingSentenceRef.current) return;

    if (!typingStarted) {
      const now = Date.now();
      typingStartMsRef.current = now;
      sentenceStartMsRef.current = now;
      setTypingStarted(true);
    }

    const target = typingTargetRef.current;
    if (!target) {
      setTypingInput(value);
      return;
    }

    const nextValue = value.length > target.length ? value.slice(0, target.length) : value;
    setTypingInput(nextValue);
  };

  useEffect(() => {
    if (!typingStarted || typingFinished || completingSentenceRef.current) return;

    const target = typingTargetRef.current;
    if (!target || typingInput.length < target.length) return;

    completingSentenceRef.current = true;
    const completedInput = typingInput.slice(0, target.length);
    completeCurrentSentence(completedInput, target, promptIndex);
    completingSentenceRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable auto-advance on full sentence
  }, [typingInput, typingStarted, typingFinished, promptIndex]);

  const handleTypingKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const resetTypingTest = () => {
    setTypingInput('');
    setTypingStarted(false);
    setTypingFinished(false);
    setTypingTimeLeft(TYPING_DURATION_SEC);
    setTypingElapsedSec(0);
    setPromptIndex(0);
    setTypingSession(EMPTY_TYPING_SESSION);
    setScoredPromptIndices(new Set());
    scoredPromptIndicesRef.current = new Set();
    setSentenceNotice('');
    typingStartMsRef.current = null;
    sentenceStartMsRef.current = null;
    completingSentenceRef.current = false;
    if (sentenceNoticeTimeoutRef.current) {
      clearTimeout(sentenceNoticeTimeoutRef.current);
      sentenceNoticeTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    resetTypingTest();
  }, [lang]);

  const typedLength = typingInput.length;
  const correctChars = countCorrectChars(typingInput, typingTarget);
  const accuracy = calcAccuracy(correctChars, typedLength);
  const isPracticePrompt = scoredPromptIndices.has(promptIndex);
  const sessionAccuracy = calcAccuracy(typingSession.totalCorrect, typingSession.totalTyped);
  const elapsedSec = typingStarted ? Math.max(0.1, typingElapsedSec) : 0;
  const sessionCharCount = typingSession.totalTyped + (isPracticePrompt ? 0 : typedLength);
  const sentenceElapsed = sentenceStartMsRef.current && typingStarted
    ? Math.max(0.1, (Date.now() - sentenceStartMsRef.current) / 1000)
    : elapsedSec;
  const wpm = calcWpm(sessionCharCount, elapsedSec);
  const currentWpm = calcWpm(typedLength, sentenceElapsed);

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (!isVoiceComplete) {
      setFormNotice(t.completeVoiceFirst);
      setMainTab('voice');
      return;
    }
    if (!isTypingComplete) {
      setFormNotice(t.completeTypingFirst);
      setMainTab('typing');
      return;
    }
    if (!formData.fullName.trim() || !formData.email.trim()) {
      setFormNotice(t.formRequired);
      return;
    }

    const { exerciseScores, overallScore } = buildAssessmentScores({
      exerciseMeta,
      englishConfidence: formData.englishConfidence,
      completedTraining: formData.completedTraining
    });

    setIsSaving(true);
    setFormNotice('');

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_assessment',
          email: formData.email.trim(),
          full_name: formData.fullName.trim(),
          english_confidence: Number(formData.englishConfidence),
          completed_training: formData.completedTraining,
          notes: formData.notes.trim(),
          exercise_scores: exerciseScores,
          exercise_meta: exerciseMeta,
          overall_score: overallScore
        })
      });

      const result = await response.json();
      if (result?.status === 'success') {
        setAssessmentSaved(true);
        setFormNotice(t.formSaved);
        setGateNotice('');
      } else {
        setFormNotice(result?.message || t.formSaveError);
      }
    } catch {
      setFormNotice(t.formSaveError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className={`info-section assessment-section fade-in-section${isHandheld ? ' is-handheld' : ''}`}>
      <div className="glass-panel assessment-panel page-panel">
        {isHandheld && typeof onApply === 'function' ? (
          <div className="assessment-mobile-apply-bar">
            <p className="assessment-mobile-apply-hint">{t.skipToApplyHint}</p>
            <button
              type="button"
              className="apply-btn assessment-apply-btn assessment-mobile-apply-cta"
              onClick={() => {
                if (typeof onApply === 'function') onApply();
              }}
            >
              {t.skipToApply}
            </button>
          </div>
        ) : null}

        <div className="assessment-hero">
          <div className="assessment-hero-top">
            <span className="assessment-eyebrow">{t.eyebrow}</span>
            <span className="assessment-progress-chip">
              {formatProgress(t.progressNote, completedCount, EXERCISE_IDS.length)}
            </span>
          </div>
          <h2 className="assessment-title">{t.title}</h2>
          <p className="assessment-lead">{t.lead}</p>
          <div className="assessment-progress-bar" role="progressbar" aria-valuenow={completedCount} aria-valuemin={0} aria-valuemax={EXERCISE_IDS.length}>
            <div
              className="assessment-progress-fill"
              style={{ width: `${(completedCount / EXERCISE_IDS.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="assessment-main-tabs" role="tablist" aria-label={t.title}>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'voice'}
            className={`assessment-main-tab${mainTab === 'voice' ? ' is-active' : ''}${isVoiceComplete ? ' is-done' : ''}`}
            onClick={() => selectMainTab('voice')}
          >
            {t.voiceTraining}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'typing'}
            aria-disabled={!canOpenTyping}
            className={`assessment-main-tab${mainTab === 'typing' ? ' is-active' : ''}${!canOpenTyping ? ' is-locked' : ''}${isTypingComplete ? ' is-done' : ''}`}
            onClick={() => selectMainTab('typing')}
          >
            {t.typingTitle}
            {!canOpenTyping ? <span className="assessment-tab-lock">{t.tabLocked}</span> : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === 'form'}
            aria-disabled={!canOpenForm}
            className={`assessment-main-tab${mainTab === 'form' ? ' is-active' : ''}${!canOpenForm ? ' is-locked' : ''}${assessmentSaved ? ' is-done' : ''}`}
            onClick={() => selectMainTab('form')}
          >
            {t.formTitle}
            {!canOpenForm ? <span className="assessment-tab-lock">{t.tabLocked}</span> : null}
          </button>
        </div>

        {gateNotice ? <p className="assessment-gate-notice">{gateNotice}</p> : null}

        <div className={`assessment-layout${mainTab === 'voice' ? ' is-voice' : ' is-single'}`}>
          {mainTab === 'voice' && (
            <aside className="assessment-sidebar">
              <div className="assessment-sidebar-head">
                <h3>{t.voiceTraining}</h3>
              </div>
              <div className="assessment-exercise-list" role="tablist" aria-label={t.voiceTraining}>
                {exercises.map((exercise, index) => (
                  <button
                    key={exercise.id}
                    type="button"
                    role="tab"
                    aria-selected={activeId === exercise.id}
                    className={`assessment-exercise-btn${activeId === exercise.id ? ' is-active' : ''}`}
                    onClick={() => setActiveId(exercise.id)}
                  >
                    <span className="assessment-exercise-num">{index + 1}</span>
                    <span>{exercise.title}</span>
                    {exerciseMeta[exercise.id]?.recorded ? (
                      <span className="assessment-done-dot" aria-hidden="true" />
                    ) : null}
                  </button>
                ))}
              </div>
              {isVoiceComplete && (
                <p className="assessment-step-hint">{t.voiceCompleteHint}</p>
              )}
              {isVoiceComplete ? (
                <button
                  type="button"
                  className="apply-btn assessment-apply-btn"
                  onClick={() => selectMainTab('typing')}
                >
                  {t.continueToTyping}
                </button>
              ) : null}
              {typeof onApply === 'function' && (
                <button
                  type="button"
                  className={`apply-btn assessment-apply-btn${isVoiceComplete && !isHandheld ? ' assessment-apply-btn--secondary' : ''}`}
                  onClick={handleApplyClick}
                  disabled={!canApply}
                  title={!canApply ? t.completeAssessmentFirst : undefined}
                >
                  {isHandheld ? t.skipToApply : t.applyCta}
                </button>
              )}
            </aside>
          )}

          <div className="assessment-main">
            {mainTab === 'voice' && activeExercise && (
              <article className="assessment-card">
                <div className="assessment-card-head">
                  <h3>{activeExercise.title}</h3>
                  <p className="assessment-tip">{activeExercise.tip}</p>
                </div>

                <div className="assessment-script-box">
                  <span className="assessment-script-label">{t.scriptLabel}</span>
                  <p>{activeExercise.script}</p>
                </div>

                {sampleSrc && (
                  <audio
                    ref={sampleAudioRef}
                    key={sampleSrc}
                    className="assessment-professional-audio"
                    preload="metadata"
                    src={sampleSrc}
                    onLoadedData={handleSampleLoaded}
                    onCanPlay={handleSampleLoaded}
                    onError={handleSampleError}
                    onEnded={() => setIsSamplePlaying(false)}
                    onPause={() => setIsSamplePlaying(false)}
                  >
                    {t.playbackUnsupported}
                  </audio>
                )}

                <div className="assessment-actions">
                  <button
                    type="button"
                    className="assessment-action-btn"
                    onClick={playProfessionalSample}
                    disabled={!sampleReady || sampleError || isSamplePlaying}
                  >
                    {isSamplePlaying ? t.playingSample : t.listenSample}
                  </button>
                  {recordingId === activeExercise.id ? (
                    <button
                      type="button"
                      className="assessment-action-btn assessment-action-btn--record is-recording"
                      onClick={stopRecording}
                    >
                      {t.stopRecording}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="assessment-action-btn assessment-action-btn--record"
                      onClick={() => startRecording(activeExercise.id)}
                    >
                      {t.startRecording}
                    </button>
                  )}
                  {recordings[activeExercise.id] && (
                    <audio
                      className="assessment-playback"
                      controls
                      src={recordings[activeExercise.id]}
                    >
                      {t.playbackUnsupported}
                    </audio>
                  )}
                </div>

                {sampleError && (
                  <p className="assessment-sample-error">{t.sampleUnavailable}</p>
                )}
                {micError && <p className="assessment-mic-error">{micError}</p>}
              </article>
            )}

            {mainTab === 'typing' && (
              <article className="assessment-card typing-test-card">
                <div className="assessment-card-head assessment-card-head--row">
                  <div>
                    <h3>{t.typingTitle}</h3>
                    <p className="assessment-tip">{t.typingDesc}</p>
                  </div>
                  <div className="typing-toolbar">
                    <span className="typing-timer">
                      {t.typingTimeLeft}: {typingTimeLeft}s
                    </span>
                    <button type="button" className="assessment-action-btn" onClick={resetTypingTest}>
                      {t.typingReset}
                    </button>
                  </div>
                </div>

                <div className="typing-target-box">
                  <span className="assessment-script-label">{t.typingPromptLabel}</span>
                  <p className="typing-target-text">{typingTarget}</p>
                </div>

                {sentenceNotice && (
                  <p className="typing-sentence-notice">{sentenceNotice}</p>
                )}

                <textarea
                  className="typing-input"
                  id="helport-typing-test-input"
                  name="helport-typing-test-field"
                  rows="3"
                  value={typingInput}
                  onChange={(e) => handleTypingChange(e.target.value)}
                  onKeyDown={handleTypingKeyDown}
                  placeholder={t.typingPlaceholder}
                  disabled={typingFinished}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-autocomplete="none"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                />

                <div className="typing-scores-panel">
                  <div className="typing-score-card">
                    <span className="typing-score-label">{t.typingWpm}</span>
                    <strong className="typing-score-value">{typedLength > 0 ? currentWpm : wpm}</strong>
                  </div>
                  <div className="typing-score-card">
                    <span className="typing-score-label">{t.typingAccuracy}</span>
                    <strong className="typing-score-value">{typedLength > 0 ? accuracy : sessionAccuracy}%</strong>
                  </div>
                  <div className="typing-score-card">
                    <span className="typing-score-label">{t.typingTotalScore}</span>
                    <strong className="typing-score-value">{typingSession.totalScore}</strong>
                  </div>
                  <div className="typing-score-card">
                    <span className="typing-score-label">{t.typingSentences}</span>
                    <strong className="typing-score-value">{typingSession.sentencesCompleted}</strong>
                  </div>
                </div>

                {isTypingComplete && (
                  <div className="assessment-step-footer">
                    <p className="assessment-step-hint">{t.typingCompleteHint}</p>
                    <button
                      type="button"
                      className="apply-btn assessment-apply-btn"
                      onClick={() => selectMainTab('form')}
                    >
                      {t.continueToChecklist}
                    </button>
                  </div>
                )}
              </article>
            )}

            {mainTab === 'form' && (
              <form className="assessment-form" onSubmit={handleFormSubmit}>
                <div className="assessment-card-head">
                  <h3>{t.formTitle}</h3>
                  <p className="assessment-form-desc">{t.formDesc}</p>
                </div>

                {formNotice && <p className="assessment-form-notice">{formNotice}</p>}

                <div className="assessment-form-grid">
                  <label className="assessment-field">
                    <span>{t.fullName}</span>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleFormChange('fullName', e.target.value)}
                      placeholder={t.fullNamePlaceholder}
                    />
                  </label>
                  <label className="assessment-field">
                    <span>{t.email}</span>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder={t.emailPlaceholder}
                    />
                  </label>
                  <label className="assessment-field">
                    <span>{t.completedTraining}</span>
                    <select
                      value={formData.completedTraining}
                      onChange={(e) => handleFormChange('completedTraining', e.target.value)}
                    >
                      <option value="">{t.selectOption}</option>
                      <option value="yes">{t.yes}</option>
                      <option value="no">{t.no}</option>
                    </select>
                  </label>
                  <label className="assessment-field">
                    <span>{t.englishConfidence}</span>
                    <div className="assessment-range-row">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={formData.englishConfidence}
                        onChange={(e) => handleFormChange('englishConfidence', e.target.value)}
                      />
                      <span className="assessment-range-value">{formData.englishConfidence}/10</span>
                    </div>
                  </label>
                  <label className="assessment-field assessment-field--wide">
                    <span>{t.notes}</span>
                    <textarea
                      rows="2"
                      value={formData.notes}
                      onChange={(e) => handleFormChange('notes', e.target.value)}
                      placeholder={t.notesPlaceholder}
                    />
                  </label>
                </div>

                <button type="submit" className="apply-btn assessment-submit-btn" disabled={isSaving}>
                  {isSaving ? t.savingForm : t.saveForm}
                </button>

                {(assessmentSaved || isHandheld) && typeof onApply === 'function' && (
                  <button
                    type="button"
                    className="apply-btn assessment-apply-btn assessment-submit-btn"
                    onClick={handleApplyClick}
                  >
                    {isHandheld ? t.skipToApply : t.applyCta}
                  </button>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const formatProgress = (template, done, total) =>
  String(template).replace('{done}', done).replace('{total}', total);

export default AssessmentPage;
